import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import MenuBar from "../components/MenuBar";
import {
  startLoading,
  stopLoading,
  setTaskProgress,
  setTaskMessage,
  LOADING_TYPES,
} from "../loading";
import {
  DUPLICATE_MODES,
  EXPECTED_COLUMNS,
  IMPORT_MODES,
  MAX_PERSIST_UPLOAD_ERRORS,
  PREVIEW_ROW_LIMIT,
  UPLOAD_STATUS,
} from "../features/salesDataImport/constants";
import { canAccessSalesDataImport } from "../features/salesDataImport/access";
import SalesImportNav from "../features/salesDataImport/components/SalesImportNav";
import { createUploadLog } from "../features/salesDataImport/services/uploadLogsService";
import {
  persistUploadErrors,
  downloadErrorsExcel,
  downloadInvalidRowsExcel,
} from "../features/salesDataImport/services/uploadErrorsService";
import { validateAllRows } from "../features/salesDataImport/services/validationService";
import { saveSalesImportLineChunks } from "../features/salesDataImport/services/salesLineChunksService";
import { countWeeklyLineChunkRows } from "../features/salesDataImport/services/salesLineChunksReader";
import {
  createWeeklyImportDraft,
  fetchWeeklyDashboard,
  finalizeWeeklyImport,
} from "../features/salesDataImport/services/weeklyImportService";
import {
  runWeeklyImportPipeline,
  runMonthlyImportPipeline,
  WORKFLOW_STEPS,
} from "../features/salesDataImport/services/weeklyImportWorkflow";
import {
  buildMonthWeekMeta,
  buildMonthMeta,
  formatWeekRangeLabel,
  groupValidRowsByWeekOfMonth,
} from "../features/salesDataImport/utils/weekUtils";
import {
  SALES_WEEKS_PER_MONTH,
  weekDateRangeLabel,
  weekOfMonthFromDate,
} from "../utils/weeklySalesUpload";
import { formatFirebaseError } from "../features/salesDataImport/utils/firebaseError";
import {
  countRowsOutsideDateRange,
  rowMatchesDateRange,
  rowTranDateYmd,
} from "../features/salesDataImport/utils/dateParse";
import "./SalesDataImportCenter.css";

function formatBytes(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function formatMoney(n) {
  const v = Number(n) || 0;
  return v.toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

function statusLabel(status, hasWarnings = false) {
  switch (status) {
    case UPLOAD_STATUS.PARSING:
      return "Parsing…";
    case UPLOAD_STATUS.VALIDATING:
      return "Validating…";
    case UPLOAD_STATUS.CHECKING_DUPLICATES:
      return "Checking duplicates…";
    case UPLOAD_STATUS.READY:
      return "Ready to upload";
    case UPLOAD_STATUS.UPLOADING:
      return "Uploading…";
    case UPLOAD_STATUS.COMPLETE:
      return hasWarnings ? "Complete (warnings)" : "Upload complete";
    case UPLOAD_STATUS.ERROR:
      return "Error";
    default:
      return "Idle";
  }
}

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) =>
  new Date(2000, i, 1).toLocaleString("en-IN", { month: "long" })
);

const PREVIEW_COLS = [
  { key: "voucherType", label: "Voucher Type" },
  { key: "voucherNo", label: "Voucher No" },
  { key: "mVoucherNo", label: "m Voucher No" },
  { key: "billNo", label: "Bill No" },
  { key: "tranDate", label: "Trans Date" },
  { key: "customerName", label: "Customer" },
  { key: "employeeName", label: "Employee" },
  { key: "staffName", label: "Staff Name" },
  { key: "branchId", label: "BranchID" },
  { key: "itemGroup", label: "ItemGroup" },
  { key: "itemName", label: "ItemName" },
  { key: "alias", label: "Alias" },
  { key: "partNo", label: "PartNo" },
  { key: "qty", label: "Qty" },
  { key: "unitPrice", label: "UnitPrice" },
  { key: "amount", label: "Amount" },
];

export default function SalesDataImportCenterPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}") || {};
    } catch {
      return {};
    }
  }, []);

  const [dark, setDark] = useState(() => localStorage.getItem("sdic-dark") === "1");
  const [status, setStatus] = useState(UPLOAD_STATUS.IDLE);
  const [fileMeta, setFileMeta] = useState(null);
  const [parsedRows, setParsedRows] = useState([]);
  const [validation, setValidation] = useState(null);
  const [existingIds, setExistingIds] = useState(new Set());
  const [duplicateMode, setDuplicateMode] = useState(DUPLICATE_MODES.REPLACE);
  const nowInit = useMemo(() => new Date(), []);
  const [importYear, setImportYear] = useState(() => nowInit.getFullYear());
  const [importMonth, setImportMonth] = useState(() => nowInit.getMonth());
  const [importWeek, setImportWeek] = useState(() => weekOfMonthFromDate(nowInit));
  const [importMode, setImportMode] = useState(
    () => localStorage.getItem("sdic-import-mode") || IMPORT_MODES.WEEKLY
  );
  const [dashboard, setDashboard] = useState(null);
  const [uploadSummary, setUploadSummary] = useState(null);
  const [weeklyImportId, setWeeklyImportId] = useState(null);

  const [previewSearch, setPreviewSearch] = useState("");
  const [sortKey, setSortKey] = useState("billNo");
  const [sortDir, setSortDir] = useState("asc");
  const [filterValid, setFilterValid] = useState("all");
  const [excludedVoucherTypes, setExcludedVoucherTypes] = useState([]);

  const [progress, setProgress] = useState({
    current: 0,
    total: 0,
    pct: 0,
    speed: 0,
    etaSec: 0,
    label: "",
  });

  const [sessionStats, setSessionStats] = useState({
    uploaded: 0,
    failed: 0,
    duplicates: 0,
    totalAmount: 0,
  });

  const [uploadErrors, setUploadErrors] = useState([]);
  const [uploadWarnings, setUploadWarnings] = useState([]);
  const [confirmUpload, setConfirmUpload] = useState(false);
  const [missingPartNoDrafts, setMissingPartNoDrafts] = useState({});
  const [applyingPartNoFix, setApplyingPartNoFix] = useState(false);

  useEffect(() => {
    if (!canAccessSalesDataImport(user)) {
      toast.error("Access denied — Admin or Storekeeper only.");
      navigate("/dashboard", { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    localStorage.setItem("sdic-dark", dark ? "1" : "0");
  }, [dark]);

  useEffect(() => {
    localStorage.setItem("sdic-import-mode", importMode);
  }, [importMode]);

  const isMonthlyMode = importMode === IMPORT_MODES.MONTHLY;

  const selectedPeriod = useMemo(() => {
    if (isMonthlyMode) return buildMonthMeta(importYear, importMonth);
    return buildMonthWeekMeta(importYear, importMonth, importWeek);
  }, [importYear, importMonth, importWeek, isMonthlyMode]);

  const yearOptions = useMemo(() => {
    const y = new Date().getFullYear();
    return [y - 1, y, y + 1];
  }, []);

  const refreshDashboard = useCallback(() => {
    fetchWeeklyDashboard(selectedPeriod).then(setDashboard).catch(() => {});
  }, [selectedPeriod]);

  useEffect(() => {
    refreshDashboard();
  }, [refreshDashboard]);

  useEffect(() => {
    setStatus(UPLOAD_STATUS.IDLE);
    setFileMeta(null);
    setParsedRows([]);
    setValidation(null);
    setExistingIds(new Set());
    setUploadErrors([]);
    setUploadWarnings([]);
    setUploadSummary(null);
    setWeeklyImportId(null);
    setExcludedVoucherTypes([]);
    setMissingPartNoDrafts({});
    setSessionStats({ uploaded: 0, failed: 0, duplicates: 0, totalAmount: 0 });
  }, [importMode, importYear, importMonth, importWeek]);

  /** If lineChunks exist for selected period, do not leave header stuck on Error. */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (status !== UPLOAD_STATUS.ERROR && status !== UPLOAD_STATUS.IDLE) return;
      try {
        let count = 0;
        if (isMonthlyMode) {
          for (let w = 1; w <= SALES_WEEKS_PER_MONTH; w += 1) {
            count += await countWeeklyLineChunkRows(
              selectedPeriod.year,
              selectedPeriod.monthIndex,
              w
            );
          }
        } else {
          count = await countWeeklyLineChunkRows(
            selectedPeriod.year,
            selectedPeriod.monthIndex,
            selectedPeriod.weekOfMonth
          );
        }
        if (cancelled || !count) return;
        setStatus(UPLOAD_STATUS.COMPLETE);
        setUploadWarnings([]);
        setUploadSummary((prev) =>
          prev?.weekNumber === selectedPeriod.periodKey
            ? prev
            : {
                weekNumber: selectedPeriod.periodKey,
                fromDate: selectedPeriod.fromDate,
                toDate: selectedPeriod.toDate,
                uploadedRecords: count,
                duplicateRecords: 0,
                failedRecords: 0,
                totalAmount: 0,
                newRecords: count,
                updatedRecords: 0,
                skippedRecords: 0,
                excludedVoucherRecords: 0,
              }
        );
        setSessionStats((s) => ({ ...s, uploaded: count }));
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    selectedPeriod.year,
    selectedPeriod.monthIndex,
    selectedPeriod.weekOfMonth,
    selectedPeriod.periodKey,
    status,
    isMonthlyMode,
  ]);

  const firestoreDuplicateCount = useMemo(() => {
    if (!validation?.rows?.length || !existingIds.size) return 0;
    return validation.rows.filter((r) => r.valid && existingIds.has(r.docId)).length;
  }, [validation, existingIds]);

  const voucherTypeStats = useMemo(() => {
    const stats = new Map();
    for (const row of validation?.rows || []) {
      const key = String(row.voucherType || "UNKNOWN").trim() || "UNKNOWN";
      const prev = stats.get(key) || { voucherType: key, total: 0, valid: 0 };
      prev.total += 1;
      if (row.valid) prev.valid += 1;
      stats.set(key, prev);
    }
    return [...stats.values()].sort((a, b) => a.voucherType.localeCompare(b.voucherType));
  }, [validation]);

  const excludedVoucherCount = useMemo(() => {
    if (!excludedVoucherTypes.length || !validation?.rows?.length) return 0;
    const excluded = new Set(excludedVoucherTypes);
    return validation.rows.filter((r) =>
      excluded.has(String(r.voucherType || "UNKNOWN").trim() || "UNKNOWN")
    ).length;
  }, [excludedVoucherTypes, validation]);

  const outOfRangeRowCount = useMemo(() => {
    if (!validation?.rows?.length || !selectedPeriod) return 0;
    return countRowsOutsideDateRange(
      validation.rows,
      selectedPeriod.fromDate,
      selectedPeriod.toDate
    );
  }, [validation, selectedPeriod]);

  const filteredValidationRows = useMemo(() => {
    if (!validation?.rows?.length) return [];
    let rows = validation.rows;
    if (excludedVoucherTypes.length) {
      const excluded = new Set(excludedVoucherTypes);
      rows = rows.filter(
        (r) => !excluded.has(String(r.voucherType || "UNKNOWN").trim() || "UNKNOWN")
      );
    }
    if (selectedPeriod?.fromDate && selectedPeriod?.toDate) {
      rows = rows.filter((r) =>
        rowMatchesDateRange(r, selectedPeriod.fromDate, selectedPeriod.toDate)
      );
    }
    return rows;
  }, [validation, excludedVoucherTypes, selectedPeriod]);

  const filteredValidCount = useMemo(
    () => filteredValidationRows.filter((r) => r.valid).length,
    [filteredValidationRows]
  );

  const filteredInvalidCount = useMemo(
    () => filteredValidationRows.filter((r) => !r.valid).length,
    [filteredValidationRows]
  );
  const filteredInvalidRows = useMemo(
    () => filteredValidationRows.filter((r) => !r.valid),
    [filteredValidationRows]
  );
  const missingPartNoRows = useMemo(
    () =>
      (validation?.rows || []).filter(
        (r) =>
          !String(r.partNo || "").trim() &&
          !String(r.itemName || "").trim() &&
          (r.errors || []).some((e) => String(e).includes("PartNo / ItemName"))
      ),
    [validation]
  );

  const filteredDuplicateInFileCount = useMemo(
    () => filteredValidationRows.filter((r) => r.duplicateInFile).length,
    [filteredValidationRows]
  );

  const filteredFirestoreDuplicateCount = useMemo(() => {
    if (!filteredValidationRows.length || !existingIds.size) return 0;
    return filteredValidationRows.filter((r) => r.valid && existingIds.has(r.docId)).length;
  }, [filteredValidationRows, existingIds]);

  const applyMissingPartNoFixes = async () => {
    if (!validation?.rows?.length) return;
    const updates = Object.entries(missingPartNoDrafts).filter(([, v]) => String(v || "").trim());
    if (!updates.length) {
      toast.error("Enter PartNo values first.");
      return;
    }
    const draftMap = new Map(updates.map(([k, v]) => [Number(k), String(v).trim()]));

    setApplyingPartNoFix(true);
    try {
      setStatus(UPLOAD_STATUS.VALIDATING);
      const patchedRows = validation.rows.map((r) => {
        const key = Number(r._rowIndex || 0);
        if (!draftMap.has(key)) return { ...r };
        return { ...r, partNo: draftMap.get(key) };
      });
      const revalidated = await validateAllRows(patchedRows, (done, total) => {
        setProgress((p) => ({
          ...p,
          current: done,
          total,
          pct: total ? Math.round((done / total) * 100) : 0,
          label: `Re-validating ${done.toLocaleString()} / ${total.toLocaleString()}`,
        }));
      });
      setValidation(revalidated);
      setExistingIds(new Set());
      setMissingPartNoDrafts({});
      setStatus(UPLOAD_STATUS.READY);
      toast.success("Items updated and revalidated.");
    } catch (e) {
      setStatus(UPLOAD_STATUS.ERROR);
      toast.error(e?.message || "Failed to apply PartNo edits.");
    } finally {
      setApplyingPartNoFix(false);
    }
  };

  const fileSummary = useMemo(() => {
    const total = parsedRows.length;
    const invalid = validation?.invalidCount ?? 0;
    const dupFile = validation?.duplicateInFileCount ?? 0;
    const dupFs = firestoreDuplicateCount;
    const amount = (validation?.rows || [])
      .filter((r) => r.valid)
      .reduce((s, r) => s + (Number(r.amount) || 0), 0);
    return {
      total,
      uploaded: sessionStats.uploaded,
      failed: sessionStats.failed + invalid,
      duplicates: dupFile + dupFs + sessionStats.duplicates,
      totalAmount: sessionStats.totalAmount || amount,
    };
  }, [parsedRows, validation, firestoreDuplicateCount, sessionStats]);

  const activeWorkflowStep = useMemo(() => {
    if (status === UPLOAD_STATUS.COMPLETE) return UPLOAD_STATUS.COMPLETE;
    if (status === UPLOAD_STATUS.UPLOADING) return UPLOAD_STATUS.UPLOADING;
    if (status === UPLOAD_STATUS.READY && validation) return "preview";
    if (status === UPLOAD_STATUS.VALIDATING || status === UPLOAD_STATUS.PARSING) {
      return status;
    }
    if (fileMeta) return UPLOAD_STATUS.PARSING;
    return "file";
  }, [status, validation, fileMeta]);

  const previewRows = useMemo(() => {
    let rows = (validation?.rows || parsedRows).slice(0, PREVIEW_ROW_LIMIT);
    const term = previewSearch.trim().toLowerCase();
    if (term) {
      rows = rows.filter(
        (r) =>
          String(r.voucherNo || "").toLowerCase().includes(term) ||
          String(r.mVoucherNo || "").toLowerCase().includes(term) ||
          String(r.billNo || "").toLowerCase().includes(term) ||
          String(r.customerName || "").toLowerCase().includes(term) ||
          String(r.branchId || "").toLowerCase().includes(term) ||
          String(r.partNo || "").toLowerCase().includes(term) ||
          String(r.alias || "").toLowerCase().includes(term)
      );
    }
    if (filterValid === "valid") rows = rows.filter((r) => r.valid);
    if (filterValid === "invalid") rows = rows.filter((r) => !r.valid);
    rows = [...rows].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      const cmp =
        typeof av === "number" && typeof bv === "number"
          ? av - bv
          : String(av ?? "").localeCompare(String(bv ?? ""), undefined, { numeric: true });
      return sortDir === "asc" ? cmp : -cmp;
    });
    return rows;
  }, [validation, parsedRows, previewSearch, sortKey, sortDir, filterValid]);

  const processFile = useCallback(
    async (file) => {
      if (!file) return;

      setStatus(UPLOAD_STATUS.PARSING);
      setValidation(null);
      setExistingIds(new Set());
      setUploadErrors([]);
      setUploadWarnings([]);
      setUploadSummary(null);
      setWeeklyImportId(null);
      setExcludedVoucherTypes([]);
      setMissingPartNoDrafts({});
      setSessionStats({ uploaded: 0, failed: 0, duplicates: 0, totalAmount: 0 });

      const taskId = startLoading({
        type: LOADING_TYPES.IMPORT,
        overlay: true,
        message: "Processing import file…",
      });

      try {
        const runPipeline = isMonthlyMode ? runMonthlyImportPipeline : runWeeklyImportPipeline;
        const pipeline = await runPipeline(file, selectedPeriod, (step, done, total) => {
          setStatus(step);
          const labels = {
            [UPLOAD_STATUS.PARSING]: "Reading Excel…",
            [UPLOAD_STATUS.VALIDATING]: `Validating ${done.toLocaleString()} / ${total.toLocaleString()}`,
            [UPLOAD_STATUS.CHECKING_DUPLICATES]: `Checking duplicates ${done.toLocaleString()} / ${total.toLocaleString()}`,
            [UPLOAD_STATUS.READY]: "Ready for preview",
          };
          const label = labels[step] || step;
          setProgress({
            current: done,
            total: total || 1,
            pct: total ? Math.round((done / total) * 100) : 0,
            speed: 0,
            etaSec: 0,
            label,
          });
          if (total > 0) {
            setTaskProgress(taskId, done, total, label, "records");
          } else {
            setTaskMessage(taskId, label);
          }
        });

        setParsedRows(pipeline.rows);
        setFileMeta(pipeline.fileMeta);
        setValidation(pipeline.validation);
        setExistingIds(pipeline.existingIds);
        setStatus(UPLOAD_STATUS.READY);
        toast.success(
          `${isMonthlyMode ? "Monthly" : "Weekly"} import ready: ${pipeline.summary.validCount.toLocaleString()} valid, ${pipeline.summary.duplicateInFirestore.toLocaleString()} already in Firestore.`
        );
      } catch (e) {
        setStatus(UPLOAD_STATUS.ERROR);
        toast.error(e?.message || "Failed to process file.");
      } finally {
        stopLoading(taskId);
      }
    },
    [selectedPeriod, isMonthlyMode]
  );

  const startImport = () => fileInputRef.current?.click();

  const handleUpload = async () => {
    if (!validation || status === UPLOAD_STATUS.UPLOADING) return;
    const validRows = filteredValidationRows.filter((r) => r.valid);
    if (!validRows.length) {
      toast.error("No valid records to upload.");
      return;
    }

    setConfirmUpload(false);
    setStatus(UPLOAD_STATUS.UPLOADING);
    const start = performance.now();
    let draft = null;

    const uploadTaskId = startLoading({
      type: LOADING_TYPES.UPLOAD,
      overlay: true,
      message: "Uploading sales data…",
      total: validRows.length,
    });

    const pushUploadProgress = (patch) => {
      setProgress((p) => ({ ...p, ...patch }));
      const total = patch.total ?? validRows.length;
      const label = patch.label ?? "Uploading…";
      if (total > 0 && (patch.current != null || patch.pct != null)) {
        const done = patch.current != null ? patch.current : Math.round(((patch.pct || 0) / 100) * total);
        setTaskProgress(uploadTaskId, done, total, label, "records");
      } else if (patch.label) {
        setTaskMessage(uploadTaskId, label);
      }
    };

    try {
      const uploadedBy = user.name || user.email || user.uid || "unknown";
      const audit = {
        periodKey: selectedPeriod.periodKey,
        weekNumber: selectedPeriod.periodKey,
        fromDate: selectedPeriod.fromDate,
        toDate: selectedPeriod.toDate,
        year: selectedPeriod.year,
        monthIndex: selectedPeriod.monthIndex,
        weekOfMonth: selectedPeriod.weekOfMonth,
        importType: isMonthlyMode ? IMPORT_MODES.MONTHLY : IMPORT_MODES.WEEKLY,
        fileName: fileMeta?.name || "",
      };

      pushUploadProgress({ label: "Creating upload batch…" });
      draft = await createWeeklyImportDraft({
        weekNumber: audit.weekNumber,
        periodKey: audit.periodKey,
        year: audit.year,
        monthIndex: audit.monthIndex,
        weekOfMonth: audit.weekOfMonth,
        fromDate: audit.fromDate,
        toDate: audit.toDate,
        uploadedBy,
        fileName: audit.fileName,
        duplicateMode,
        totalRecords: parsedRows.length,
      });
      setWeeklyImportId(draft.id);
      audit.importBatchId = draft.id;

      const warnings = [];
      let written = 0;
      const weekBreakdown = [];

      if (isMonthlyMode) {
        const buckets = groupValidRowsByWeekOfMonth(
          filteredValidationRows,
          selectedPeriod.year,
          selectedPeriod.monthIndex
        );
        const weeksToSave = Array.from({ length: SALES_WEEKS_PER_MONTH }, (_, i) => i + 1).filter(
          (w) => buckets[w]?.length
        );
        if (!weeksToSave.length) {
          throw new Error(
            `No rows with TranDate in ${selectedPeriod.label}. Check Excel dates or selected month.`
          );
        }

        for (let i = 0; i < weeksToSave.length; i += 1) {
          const w = weeksToSave[i];
          const weekMeta = buildMonthWeekMeta(selectedPeriod.year, selectedPeriod.monthIndex, w);
          const weekRows = buckets[w];
          pushUploadProgress({
            label: `Saving week ${w} (${i + 1}/${weeksToSave.length})…`,
            pct: Math.round((i / weeksToSave.length) * 100),
            total: validRows.length,
            current: Math.round((i / weeksToSave.length) * validRows.length),
          });

          const weekAudit = {
            ...audit,
            periodKey: weekMeta.periodKey,
            weekNumber: weekMeta.periodKey,
            fromDate: weekMeta.fromDate,
            toDate: weekMeta.toDate,
            weekOfMonth: w,
            importBatchId: draft.id,
          };

          const chunkResult = await saveSalesImportLineChunks({
            year: selectedPeriod.year,
            monthIndex: selectedPeriod.monthIndex,
            weekOfMonth: w,
            rows: weekRows,
            uploadedBy,
            audit: weekAudit,
            onProgress: (done, total) => {
              const now = performance.now();
              const speed = total ? done / ((now - start) / 1000 || 0.001) : 0;
              const pct = weeksToSave.length
                ? Math.round(((i + done / (total || 1)) / weeksToSave.length) * 100)
                : 0;
              const overallDone = Math.round(
                ((i + done / (total || 1)) / weeksToSave.length) * validRows.length
              );
              pushUploadProgress({
                current: overallDone,
                total: validRows.length,
                pct,
                speed: Math.round(speed),
                etaSec: 0,
                label: `Week ${w} — line chunks ${done} / ${total}`,
              });
            },
          });
          written += chunkResult?.rowCount || 0;
          weekBreakdown.push({ week: w, rowCount: chunkResult?.rowCount || 0 });
        }
      } else {
        pushUploadProgress({ label: "Saving line chunks…", pct: 0, current: 0, total: validRows.length });
        let chunkResult;
        try {
          chunkResult = await saveSalesImportLineChunks({
            year: selectedPeriod.year,
            monthIndex: selectedPeriod.monthIndex,
            weekOfMonth: selectedPeriod.weekOfMonth,
            rows: filteredValidationRows,
            uploadedBy,
            audit,
            onProgress: (done, total) => {
              const now = performance.now();
              const speed = total ? done / ((now - start) / 1000 || 0.001) : 0;
              const pct = total ? Math.round((done / total) * 100) : 0;
              pushUploadProgress({
                current: done,
                total: total || validRows.length,
                pct,
                speed: Math.round(speed),
                etaSec: 0,
                label: `Line chunks ${done} / ${total}`,
              });
            },
          });
        } catch (chunkErr) {
          throw new Error(formatFirebaseError(chunkErr));
        }
        written = chunkResult?.rowCount || 0;
      }

      if (written === 0 && validRows.length > 0) {
        throw new Error(
          "No records were saved to lineChunks. Check Firestore rules for sales_import_months."
        );
      }

      const result = {
        success: written,
        updated: 0,
        skipped: 0,
        failed: 0,
        errors: [],
      };

      const allErrors = [
        ...filteredValidationRows
          .filter((r) => !r.valid)
          .map((r) => ({
            billNo: r.billNo,
            partNo: r.partNo,
            reason: (r.errors || []).join("; "),
            timestamp: new Date().toISOString(),
          })),
        ...result.errors,
      ];

      if (allErrors.length) {
        try {
          await persistUploadErrors(allErrors, MAX_PERSIST_UPLOAD_ERRORS);
        } catch {
          toast.error("Some rows failed; error log could not be saved to Firestore.");
        }
        setUploadErrors(allErrors.slice(0, MAX_PERSIST_UPLOAD_ERRORS));
      }

      const uploadedRecords = result.success + result.updated;
      const duplicateRecords = result.skipped + filteredFirestoreDuplicateCount;
      const failedRecords = filteredInvalidCount + result.failed;
      const totalAmount = validRows.reduce((s, r) => s + (Number(r.amount) || 0), 0);

      try {
        await finalizeWeeklyImport(draft.id, {
          status: warnings.length ? "completed_with_warnings" : "completed",
          uploadedRecords,
          duplicateRecords,
          failedRecords,
          totalSalesAmount: totalAmount,
          totalRecords: filteredValidationRows.length,
        });
      } catch (finErr) {
        warnings.push(`Weekly import log: ${formatFirebaseError(finErr)}`);
      }

      try {
        await createUploadLog({
          fileName: fileMeta?.name,
          uploadedBy,
          totalRecords: filteredValidationRows.length,
          successRecords: uploadedRecords,
          failedRecords,
          duplicateRecords,
          totalSalesAmount: totalAmount,
          weekNumber: audit.weekNumber,
          periodKey: audit.periodKey,
          year: audit.year,
          month: audit.monthIndex + 1,
          weekOfMonth: audit.weekOfMonth,
          fromDate: audit.fromDate,
          toDate: audit.toDate,
          weeklyImportId: draft.id,
          duplicateMode,
        });
      } catch (logErr) {
        warnings.push(`Upload log: ${formatFirebaseError(logErr)}`);
      }

      setUploadWarnings(warnings);
      setSessionStats({
        uploaded: uploadedRecords,
        failed: failedRecords,
        duplicates: result.skipped,
        totalAmount,
      });
      setUploadSummary({
        weekNumber: audit.weekNumber,
        fromDate: audit.fromDate,
        toDate: audit.toDate,
        uploadedRecords,
        duplicateRecords,
        failedRecords,
        totalAmount,
        newRecords: result.success,
        updatedRecords: result.updated,
        skippedRecords: result.skipped,
        excludedVoucherRecords: excludedVoucherCount,
        excludedOutOfRangeRecords: outOfRangeRowCount,
        importType: isMonthlyMode ? IMPORT_MODES.MONTHLY : IMPORT_MODES.WEEKLY,
        weekBreakdown,
      });
      setStatus(UPLOAD_STATUS.COMPLETE);
      refreshDashboard();
      if (warnings.length) {
        toast.error(
          `Saved ${written.toLocaleString()} transaction(s). Extra steps failed — see warnings below.`,
          { duration: 10000 }
        );
      } else {
        toast.success(
          isMonthlyMode
            ? `Monthly import complete: ${result.success.toLocaleString()} rows saved across ${weekBreakdown.length} week(s).`
            : `Weekly import complete: ${result.success} new, ${result.updated} updated, ${result.skipped} skipped.`
        );
      }
    } catch (e) {
      const msg = formatFirebaseError(e);
      let savedCount = 0;
      try {
        if (isMonthlyMode) {
          for (let w = 1; w <= SALES_WEEKS_PER_MONTH; w += 1) {
            savedCount += await countWeeklyLineChunkRows(
              selectedPeriod.year,
              selectedPeriod.monthIndex,
              w
            );
          }
        } else {
          savedCount = await countWeeklyLineChunkRows(
            selectedPeriod.year,
            selectedPeriod.monthIndex,
            selectedPeriod.weekOfMonth
          );
        }
      } catch {
        savedCount = 0;
      }

      if (savedCount > 0) {
        const totalAmount = validRows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
        setUploadWarnings([msg]);
        setUploadSummary({
          weekNumber: selectedPeriod.periodKey,
          fromDate: selectedPeriod.fromDate,
          toDate: selectedPeriod.toDate,
          uploadedRecords: savedCount,
          duplicateRecords: 0,
          failedRecords: filteredInvalidCount,
          totalAmount,
          newRecords: savedCount,
          updatedRecords: 0,
          skippedRecords: 0,
          excludedVoucherRecords: excludedVoucherCount,
        });
        setSessionStats({
          uploaded: savedCount,
          failed: filteredInvalidCount,
          duplicates: 0,
          totalAmount,
        });
        setStatus(UPLOAD_STATUS.COMPLETE);
        if (draft?.id) {
          await finalizeWeeklyImport(draft.id, {
            status: "completed_with_warnings",
            uploadedRecords: savedCount,
            duplicateRecords: 0,
            failedRecords: filteredInvalidCount,
            totalSalesAmount: totalAmount,
            totalRecords: filteredValidationRows.length,
          }).catch(() => {});
        }
        toast.error(
          `Saved ${savedCount.toLocaleString()} lines to lineChunks, but: ${msg}`,
          { duration: 10000 }
        );
        refreshDashboard();
      } else {
        if (draft?.id) {
          await finalizeWeeklyImport(draft.id, {
            status: "failed",
            uploadedRecords: 0,
            duplicateRecords: 0,
            failedRecords: parsedRows.length,
            totalSalesAmount: 0,
            totalRecords: parsedRows.length,
          }).catch(() => {});
        }
        setStatus(UPLOAD_STATUS.ERROR);
        toast.error(msg, { duration: 8000 });
        console.error("Sales import upload failed:", e);
      }
    } finally {
      stopLoading(uploadTaskId);
    }
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.ctrlKey && e.key === "u") {
        e.preventDefault();
        fileInputRef.current?.click();
      }
      if (e.ctrlKey && e.key === "Enter" && status === UPLOAD_STATUS.READY) {
        e.preventDefault();
        setConfirmUpload(true);
      }
      if (e.key === "Escape") setConfirmUpload(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [status]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  return (
    <div className={`sdic-root${dark ? " sdic-dark" : ""}`}>
      <MenuBar />
      <header className="sdic-header">
        <div>
          <h1>Sales Data Import Center</h1>
          <p>Weekly or monthly SQL Excel import to Firestore</p>
        </div>
        <div className="sdic-header-meta">
          <span>{user.name || user.email || "User"}</span>
          <span
            className={`sdic-badge${
              status === UPLOAD_STATUS.COMPLETE && uploadWarnings.length
                ? " sdic-badge-warn"
                : status === UPLOAD_STATUS.ERROR
                  ? " sdic-badge-error"
                  : status === UPLOAD_STATUS.COMPLETE
                    ? " sdic-badge-ok"
                    : ""
            }`}
          >
            {statusLabel(status, uploadWarnings.length > 0)}
          </span>
          <button type="button" className="sdic-btn sdic-btn-secondary" onClick={() => setDark((d) => !d)}>
            {dark ? "Light" : "Dark"} mode
          </button>
          {(status === UPLOAD_STATUS.ERROR ||
            (status === UPLOAD_STATUS.COMPLETE && uploadWarnings.length)) && (
            <button
              type="button"
              className="sdic-btn sdic-btn-secondary"
              onClick={() => {
                setUploadWarnings([]);
                if (validation) setStatus(UPLOAD_STATUS.READY);
                else setStatus(UPLOAD_STATUS.IDLE);
              }}
            >
              Dismiss
            </button>
          )}
        </div>
      </header>

      <div className="sdic-body">
        <SalesImportNav active="import" showBranchFilter={false} />

        <section className="sdic-panel">
          <h2 className="sdic-panel-title">Upload Dashboard</h2>
          <div className="sdic-cards">
            <div className="sdic-card">
              <div className="sdic-card-label">Last Upload Date</div>
              <div className="sdic-card-value" style={{ fontSize: "0.9rem" }}>
                {dashboard?.lastUploadDate || "—"}
              </div>
            </div>
            {!isMonthlyMode && (
              <>
                <div className="sdic-card">
                  <div className="sdic-card-label">Current Week Status</div>
                  <div className="sdic-card-value" style={{ fontSize: "0.95rem" }}>
                    <span
                      className={`sdic-status-pill ${
                        dashboard?.currentWeekStatus === "Completed"
                          ? "sdic-status-complete"
                          : "sdic-status-pending"
                      }`}
                    >
                      {dashboard?.currentWeekStatus || "—"}
                    </span>
                  </div>
                  <div style={{ fontSize: "0.7rem", color: "var(--sdic-muted)", marginTop: "0.35rem" }}>
                    {dashboard?.weekNumber || ""}
                  </div>
                </div>
                <div className="sdic-card">
                  <div className="sdic-card-label">Imported This Week</div>
                  <div className="sdic-card-value">{(dashboard?.recordsThisWeek ?? 0).toLocaleString()}</div>
                </div>
                <div className="sdic-card">
                  <div className="sdic-card-label">Sales This Week</div>
                  <div className="sdic-card-value">₹ {formatMoney(dashboard?.salesThisWeek)}</div>
                </div>
              </>
            )}
            <div className="sdic-card">
              <div className="sdic-card-label">Imported This Month</div>
              <div className="sdic-card-value">{(dashboard?.recordsThisMonth ?? 0).toLocaleString()}</div>
            </div>
            <div className="sdic-card">
              <div className="sdic-card-label">Sales This Month</div>
              <div className="sdic-card-value">₹ {formatMoney(dashboard?.salesThisMonth)}</div>
            </div>
          </div>
        </section>

        <div className="sdic-workflow">
          {WORKFLOW_STEPS.map((step) => {
            const idx = WORKFLOW_STEPS.findIndex((s) => s.id === activeWorkflowStep);
            const stepIdx = WORKFLOW_STEPS.findIndex((s) => s.id === step.id);
            const cls =
              step.id === activeWorkflowStep
                ? "sdic-workflow-step sdic-step-active"
                : stepIdx < idx
                  ? "sdic-workflow-step sdic-step-done"
                  : "sdic-workflow-step";
            return (
              <div key={step.id} className={cls}>
                {step.label}
              </div>
            );
          })}
        </div>

        <section className="sdic-panel">
          <h2 className="sdic-panel-title">Upload mode &amp; period</h2>
          <div className="sdic-mode-toggle" role="group" aria-label="Upload mode">
            <button
              type="button"
              className={`sdic-mode-btn${!isMonthlyMode ? " sdic-mode-btn-active" : ""}`}
              onClick={() => setImportMode(IMPORT_MODES.WEEKLY)}
            >
              Weekly upload
            </button>
            <button
              type="button"
              className={`sdic-mode-btn${isMonthlyMode ? " sdic-mode-btn-active" : ""}`}
              onClick={() => setImportMode(IMPORT_MODES.MONTHLY)}
            >
              Monthly upload
            </button>
          </div>
          <p style={{ fontSize: "0.85rem", color: "var(--sdic-muted)", marginTop: "0.75rem" }}>
            {isMonthlyMode ? (
              <>
                Choose the calendar month, then upload the full month Excel file. Rows are split into
                weeks 1–5 by <strong>Trans Date</strong> and saved to Firestore{" "}
                <strong>lineChunks</strong>. Re-upload replaces each affected week slice only.
              </>
            ) : (
              <>
                Choose the calendar month and week (1–5), then upload the Excel file. Data is saved as
                Firestore <strong>lineChunks</strong> for that week ({selectedPeriod.fromDate} →{" "}
                {selectedPeriod.toDate}) — built for 30,000+ lines per week. Reports read chunks by week
                date, not row-by-row. Re-upload replaces that week only.
              </>
            )}
          </p>
          <div className="sdic-week-banner">
            <label>
              Year
              <select value={importYear} onChange={(e) => setImportYear(Number(e.target.value))}>
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Month
              <select value={importMonth} onChange={(e) => setImportMonth(Number(e.target.value))}>
                {MONTH_OPTIONS.map((name, idx) => (
                  <option key={name} value={idx}>
                    {name}
                  </option>
                ))}
              </select>
            </label>
            {!isMonthlyMode && (
              <label>
                Week
                <select value={importWeek} onChange={(e) => setImportWeek(Number(e.target.value))}>
                  {Array.from({ length: SALES_WEEKS_PER_MONTH }, (_, i) => i + 1).map((w) => (
                    <option key={w} value={w}>
                      Week {w} ({weekDateRangeLabel(importYear, importMonth, w)})
                    </option>
                  ))}
                </select>
              </label>
            )}
            <span style={{ fontSize: "0.85rem", color: "var(--sdic-muted)", flex: "1 1 100%" }}>
              Period: <strong>{selectedPeriod.periodKey}</strong> ·{" "}
              {formatWeekRangeLabel(selectedPeriod.fromDate, selectedPeriod.toDate)}
            </span>
          </div>
        </section>

        <section className="sdic-panel">
          <h2 className="sdic-panel-title">
            {isMonthlyMode ? "Monthly sales import" : "Weekly sales import"}
          </h2>
          <p style={{ fontSize: "0.85rem", color: "var(--sdic-muted)", marginTop: 0 }}>
            Read Excel → Validate → Preview → Upload to <strong>lineChunks</strong>
            {isMonthlyMode ? " (auto-split by week from Trans Date)." : " (fast weekly storage)."}
          </p>
          <div className="sdic-upload-row">
            <button
              type="button"
              className="sdic-btn sdic-btn-primary"
              disabled={status === UPLOAD_STATUS.UPLOADING || status === UPLOAD_STATUS.PARSING}
              onClick={startImport}
            >
              Upload Excel File
            </button>
            <span className="sdic-upload-hint">Supported: .xlsx, .xls only</span>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) processFile(f);
              e.target.value = "";
            }}
          />
          {fileMeta && (
            <div className="sdic-file-meta">
              <div>
                <strong>File</strong>
                <br />
                {fileMeta.name}
              </div>
              <div>
                <strong>Size</strong>
                <br />
                {formatBytes(fileMeta.size)}
              </div>
              <div>
                <strong>Rows</strong>
                <br />
                {fileMeta.rows.toLocaleString()}
              </div>
            </div>
          )}
          <p className="sdic-shortcut-hint">Shortcuts: Ctrl+U browse · Ctrl+Enter upload (when ready)</p>
          {fileMeta && (
            <div className="sdic-cards" style={{ marginTop: "1rem" }}>
              <div className="sdic-card">
                <div className="sdic-card-label">File rows</div>
                <div className="sdic-card-value">{fileSummary.total.toLocaleString()}</div>
              </div>
              <div className="sdic-card">
                <div className="sdic-card-label">Session uploaded</div>
                <div className="sdic-card-value">{fileSummary.uploaded.toLocaleString()}</div>
              </div>
              <div className="sdic-card">
                <div className="sdic-card-label">Failed</div>
                <div className="sdic-card-value">{fileSummary.failed.toLocaleString()}</div>
              </div>
              <div className="sdic-card">
                <div className="sdic-card-label">Duplicates</div>
                <div className="sdic-card-value">{fileSummary.duplicates.toLocaleString()}</div>
              </div>
            </div>
          )}
        </section>

        <section className="sdic-panel">
          <h2 className="sdic-panel-title">Expected Excel Columns</h2>
          <p style={{ fontSize: "0.85rem", color: "var(--sdic-muted)", margin: 0 }}>
            {EXPECTED_COLUMNS.join(" · ")}
          </p>
        </section>

        {validation && (
          <>
            <section className="sdic-panel">
              <h2 className="sdic-panel-title">Validation Report</h2>
              <div className="sdic-validation-grid">
                <div className="sdic-val-box sdic-val-valid">
                  Valid (selected)
                  <br />
                  {filteredValidCount.toLocaleString()}
                </div>
                <div className="sdic-val-box sdic-val-invalid">
                  Invalid (selected)
                  <br />
                  {filteredInvalidCount.toLocaleString()}
                </div>
                <div className="sdic-val-box sdic-val-dup">
                  Duplicates (selected)
                  <br />
                  {(filteredDuplicateInFileCount + filteredFirestoreDuplicateCount).toLocaleString()}
                </div>
              </div>

              <h3 className="sdic-panel-title" style={{ fontSize: "0.95rem" }}>
                Remove unnecessary vouchers before upload
              </h3>
              <div className="sdic-radio-group">
                {voucherTypeStats.map((v) => (
                  <label key={v.voucherType}>
                    <input
                      type="checkbox"
                      checked={excludedVoucherTypes.includes(v.voucherType)}
                      onChange={(e) =>
                        setExcludedVoucherTypes((prev) =>
                          e.target.checked
                            ? [...prev, v.voucherType]
                            : prev.filter((x) => x !== v.voucherType)
                        )
                      }
                    />
                    Exclude <strong>{v.voucherType}</strong> (rows: {v.total.toLocaleString()}, valid:{" "}
                    {v.valid.toLocaleString()})
                  </label>
                ))}
                {excludedVoucherCount > 0 && (
                  <span style={{ fontSize: "0.82rem", color: "var(--sdic-warning)" }}>
                    Excluding {excludedVoucherCount.toLocaleString()} row(s) from upload.
                  </span>
                )}
              </div>

              {outOfRangeRowCount > 0 && (
                <p style={{ fontSize: "0.85rem", color: "var(--sdic-warning)", margin: "0.5rem 0 0" }}>
                  {outOfRangeRowCount.toLocaleString()} row(s) have Trans Date outside{" "}
                  <strong>
                    {selectedPeriod.fromDate} → {selectedPeriod.toDate}
                  </strong>{" "}
                  and will <strong>not</strong> be uploaded (e.g. {selectedPeriod.fromDate === "2026-05-01" ? "2026-04-30" : "earlier/later dates"} in the file).
                </p>
              )}

              <h3 className="sdic-panel-title" style={{ fontSize: "0.95rem" }}>
                Duplicate handling
              </h3>
              <div className="sdic-radio-group">
                <label>
                  <input
                    type="radio"
                    name="dupMode"
                    checked={duplicateMode === DUPLICATE_MODES.SKIP}
                    onChange={() => setDuplicateMode(DUPLICATE_MODES.SKIP)}
                  />
                  Skip existing records (BillNo_ProductID)
                </label>
                <label>
                  <input
                    type="radio"
                    name="dupMode"
                    checked={duplicateMode === DUPLICATE_MODES.UPDATE}
                    onChange={() => setDuplicateMode(DUPLICATE_MODES.UPDATE)}
                  />
                  Update existing records
                </label>
                <label>
                  <input
                    type="radio"
                    name="dupMode"
                    checked={duplicateMode === DUPLICATE_MODES.REPLACE}
                    onChange={() => setDuplicateMode(DUPLICATE_MODES.REPLACE)}
                  />
                  Replace existing records
                </label>
              </div>
            </section>

            <section className="sdic-panel">
              <h2 className="sdic-panel-title">Preview (first {PREVIEW_ROW_LIMIT} rows)</h2>
              <input
                className="sdic-search"
                placeholder="Search Voucher No, m Voucher No, Bill No, Customer, Part No, Alias…"
                value={previewSearch}
                onChange={(e) => setPreviewSearch(e.target.value)}
              />
              <div className="sdic-toolbar">
                <label>
                  Filter:{" "}
                  <select value={filterValid} onChange={(e) => setFilterValid(e.target.value)}>
                    <option value="all">All</option>
                    <option value="valid">Valid only</option>
                    <option value="invalid">Invalid only</option>
                  </select>
                </label>
              </div>
              <div className="sdic-table-wrap">
                <table className="sdic-table">
                  <thead>
                    <tr>
                      {PREVIEW_COLS.map((c) => (
                        <th key={c.key} onClick={() => toggleSort(c.key)}>
                          {c.label}
                          {sortKey === c.key ? (sortDir === "asc" ? " ↑" : " ↓") : ""}
                        </th>
                      ))}
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((r, i) => (
                      <tr key={r.docId || i}>
                        {PREVIEW_COLS.map((c) => (
                          <td key={c.key}>
                            {c.key === "tranDate" ? rowTranDateYmd(r) || r[c.key] || "" : r[c.key] ?? ""}
                          </td>
                        ))}
                        <td style={{ color: r.valid ? "var(--sdic-success)" : "var(--sdic-danger)" }}>
                          {r.valid ? "OK" : (r.errors || []).join("; ")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {missingPartNoRows.length > 0 && (
              <section className="sdic-panel">
                <h2 className="sdic-panel-title">Fix items with missing PartNo / ItemName</h2>
                <p style={{ fontSize: "0.83rem", color: "var(--sdic-muted)" }}>
                  Fill PartNo for these rows, then click <strong>Apply PartNo Fixes</strong> and continue upload.
                </p>
                <div className="sdic-table-wrap">
                  <table className="sdic-table">
                    <thead>
                      <tr>
                        <th>Row</th>
                        <th>VoucherType</th>
                        <th>BillNo</th>
                        <th>ProductID</th>
                        <th>Current PartNo</th>
                        <th>New PartNo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {missingPartNoRows.slice(0, 200).map((r) => (
                        <tr key={`${r._rowIndex}-${r.docId || ""}`}>
                          <td>{r._rowIndex}</td>
                          <td>{r.voucherType || ""}</td>
                          <td>{r.billNo || ""}</td>
                          <td>{r.productId || ""}</td>
                          <td>{r.partNo || "—"}</td>
                          <td>
                            <input
                              type="text"
                              className="sdic-search"
                              style={{ margin: 0, maxWidth: 180, padding: "0.35rem 0.5rem" }}
                              value={missingPartNoDrafts[r._rowIndex] ?? ""}
                              onChange={(e) =>
                                setMissingPartNoDrafts((prev) => ({
                                  ...prev,
                                  [r._rowIndex]: e.target.value,
                                }))
                              }
                              placeholder="Enter PartNo"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="sdic-toolbar">
                  <button
                    type="button"
                    className="sdic-btn sdic-btn-primary"
                    onClick={applyMissingPartNoFixes}
                    disabled={applyingPartNoFix}
                  >
                    {applyingPartNoFix ? "Applying..." : "Apply PartNo Fixes"}
                  </button>
                </div>
              </section>
            )}
          </>
        )}

        {(status === UPLOAD_STATUS.UPLOADING || progress.label) && status !== UPLOAD_STATUS.IDLE && (
          <section className="sdic-panel sdic-progress-wrap">
            <h2 className="sdic-panel-title">Upload Progress</h2>
            <div className="sdic-progress-bar">
              <div className="sdic-progress-fill" style={{ width: `${progress.pct}%` }} />
            </div>
            <div className="sdic-progress-stats">
              <span>{progress.label}</span>
              <span>{progress.pct}%</span>
              <span>Speed: ~{progress.speed} rec/s</span>
              <span>ETA: {progress.etaSec}s</span>
            </div>
          </section>
        )}

        <div className="sdic-toolbar">
          <button
            type="button"
            className="sdic-btn sdic-btn-primary"
            disabled={status !== UPLOAD_STATUS.READY && status !== UPLOAD_STATUS.COMPLETE}
            onClick={() => setConfirmUpload(true)}
          >
            Complete {isMonthlyMode ? "Monthly" : "Weekly"} Upload
          </button>
          {filteredInvalidRows.length > 0 && (
            <button
              type="button"
              className="sdic-btn sdic-btn-secondary"
              onClick={() =>
                downloadInvalidRowsExcel(
                  filteredInvalidRows,
                  `${isMonthlyMode ? "monthly" : "weekly"}-import-invalid-rows.xlsx`
                )
              }
            >
              Download invalid rows file
            </button>
          )}
          {uploadErrors.length > 0 && (
            <button
              type="button"
              className="sdic-btn sdic-btn-secondary"
              onClick={() => downloadErrorsExcel(uploadErrors)}
            >
              Download error log (Excel)
            </button>
          )}
        </div>

        {uploadSummary && status === UPLOAD_STATUS.COMPLETE && (
          <section className="sdic-panel sdic-summary-panel">
            <h2 className="sdic-panel-title">
              {uploadSummary.importType === IMPORT_MODES.MONTHLY
                ? "Monthly Import Summary"
                : "Weekly Import Summary"}
            </h2>
            <p>
              {uploadSummary.importType === IMPORT_MODES.MONTHLY ? (
                <>
                  Month <strong>{uploadSummary.weekNumber}</strong> (
                  {formatWeekRangeLabel(uploadSummary.fromDate, uploadSummary.toDate)})
                </>
              ) : (
                <>
                  Week <strong>{uploadSummary.weekNumber}</strong> (
                  {formatWeekRangeLabel(uploadSummary.fromDate, uploadSummary.toDate)})
                </>
              )}
            </p>
            {!!uploadSummary.weekBreakdown?.length && (
              <p style={{ fontSize: "0.85rem", color: "var(--sdic-muted)" }}>
                By week:{" "}
                {uploadSummary.weekBreakdown
                  .map((w) => `W${w.week}: ${w.rowCount.toLocaleString()}`)
                  .join(" · ")}
              </p>
            )}
            <div className="sdic-validation-grid">
              <div className="sdic-val-box sdic-val-valid">
                Uploaded
                <br />
                {uploadSummary.uploadedRecords.toLocaleString()}
              </div>
              <div className="sdic-val-box sdic-val-dup">
                Skipped duplicates
                <br />
                {uploadSummary.skippedRecords.toLocaleString()}
              </div>
              <div className="sdic-val-box sdic-val-invalid">
                Failed
                <br />
                {uploadSummary.failedRecords.toLocaleString()}
              </div>
            </div>
            <p>
              New: {uploadSummary.newRecords.toLocaleString()} · Updated:{" "}
              {uploadSummary.updatedRecords.toLocaleString()} · Sales: ₹{" "}
              {formatMoney(uploadSummary.totalAmount)}
            </p>
            {!!uploadSummary.excludedVoucherRecords && (
              <p style={{ fontSize: "0.82rem", color: "var(--sdic-warning)" }}>
                Voucher-excluded rows: {uploadSummary.excludedVoucherRecords.toLocaleString()}
              </p>
            )}
            {!!uploadSummary.excludedOutOfRangeRecords && (
              <p style={{ fontSize: "0.82rem", color: "var(--sdic-warning)" }}>
                TranDate outside {uploadSummary.importType === IMPORT_MODES.MONTHLY ? "month" : "week"} (
                {uploadSummary.fromDate} → {uploadSummary.toDate}):{" "}
                {uploadSummary.excludedOutOfRangeRecords.toLocaleString()}
              </p>
            )}
            {weeklyImportId && (
              <p style={{ fontSize: "0.8rem", color: "var(--sdic-muted)" }}>
                Audit batch ID: {weeklyImportId}
              </p>
            )}
            {uploadWarnings.length > 0 && (
              <div className="sdic-warnings-box">
                <strong>Post-upload warnings</strong>
                <p style={{ margin: "0.35rem 0 0", fontSize: "0.85rem" }}>
                  Some post-upload steps failed:
                </p>
                <ul>
                  {uploadWarnings.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        )}

        {confirmUpload && (
          <div
            className="sdic-panel"
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(15,23,42,0.5)",
              zIndex: 1000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            role="dialog"
          >
            <div
              className="sdic-panel"
              style={{ maxWidth: 420, margin: "1rem" }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="sdic-panel-title">
                Confirm {isMonthlyMode ? "monthly" : "weekly"} upload
              </h2>
              <p>
                Upload {filteredValidCount.toLocaleString()} valid records for{" "}
                <strong>{selectedPeriod.label}</strong>?{" "}
                {isMonthlyMode
                  ? "Rows are split by Trans Date into weeks 1–5; each affected week slice is replaced."
                  : "This replaces any existing data for this week."}
              </p>
              <p style={{ fontSize: "0.85rem" }}>
                Duplicate mode: <strong>{duplicateMode}</strong> · Existing in Firestore:{" "}
                {filteredFirestoreDuplicateCount.toLocaleString()}
              </p>
              {!!excludedVoucherTypes.length && (
                <p style={{ fontSize: "0.82rem", color: "var(--sdic-warning)" }}>
                  Excluded vouchers: {excludedVoucherTypes.join(", ")} · rows removed:{" "}
                  {excludedVoucherCount.toLocaleString()}
                </p>
              )}
              <div className="sdic-toolbar">
                <button type="button" className="sdic-btn sdic-btn-primary" onClick={handleUpload}>
                  Yes, upload
                </button>
                <button type="button" className="sdic-btn sdic-btn-secondary" onClick={() => setConfirmUpload(false)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
